"use client";

import React from "react";
import { getBadgeColorStyles } from "@/utils/colorStyles";
import { isLiveOptionId } from "@/lib/propertyRefs";

export type OptionType = "status" | "select" | "priority" | "multi_select";

export type ContentWrapMode = "wrap" | "nowrap";

interface OptionSchema {
  options?: { id?: string; name: string; color?: string }[];
}

const DEFAULT_PRIORITY_OPTIONS = [
  { id: "high", name: "High" },
  { id: "medium", name: "Medium" },
  { id: "low", name: "Low" },
];

/** Normalize a raw item (string, or {id,name} object) to an option key. */
function itemKey(item: any): string {
  if (item && typeof item === "object") return String(item.id ?? item.name ?? "");
  return String(item ?? "");
}

/**
 * Resolve an option id to its display name. An id absent from a loaded schema
 * is a deleted option — empty, never the raw id. Falls back to the raw value
 * only when the schema has no options yet (not loaded).
 */
export function resolveOptionName(schema: OptionSchema | null | undefined, value: string): string {
  const options = schema?.options;
  const opt = options?.find((o) => o && String(o.id) === String(value));
  if (opt) return opt.name ?? "";
  if (Array.isArray(options) && options.length > 0) return "";
  return value ?? "";
}

/** True when the value is an option the (loaded) schema no longer defines. */
export function isDeletedOption(schema: OptionSchema | null | undefined, value: string): boolean {
  return !isLiveOptionId(schema, value);
}

/** Normalize a multi-select value into an array of option keys. */
function toItemList(value: any): string[] {
  if (Array.isArray(value)) return value.map(itemKey).filter(Boolean);
  if (typeof value === "string") {
    return value.includes(",") ? value.split(",").map((v) => v.trim()).filter(Boolean) : (value ? [value] : []);
  }
  return value ? [itemKey(value)] : [];
}

function optionColorName(schema: OptionSchema, value: string): string {
  const opt = schema.options?.find((o) => String(o.id) === String(value));
  return opt?.color || "default";
}

export type PillVariant = "status" | "chip";
export type PillSize = "sm" | "xs";

/**
 * A single option pill. `status` = rounded with a dot; `chip` = square-ish,
 * no dot. `size` sm = text-sm/h-20 (list, inputs, headers); xs = text-xs/h-18
 * (view cards, to match the other card properties). Shared everywhere.
 */
export function OptionPill({
  colorName = "default",
  label,
  variant = "status",
  size = "sm",
  noShrink = false,
}: {
  colorName?: string;
  label: string;
  variant?: PillVariant;
  size?: PillSize;
  noShrink?: boolean;
}) {
  const styles = getBadgeColorStyles(colorName);
  const xs = size === "xs";
  const h = xs ? "h-[18px]" : "h-[20px]";
  const text = xs ? "text-xs" : "text-sm";
  const fit = noShrink ? "shrink-0" : "min-w-0 max-w-full overflow-hidden text-ellipsis";
  if (variant === "chip") {
    return (
      <div
        className={`inline-flex items-center ${fit} ${h} m-0 rounded-[3px] ${xs ? "px-[5px]" : "px-[6px]"} leading-[120%] ${text} whitespace-nowrap`}
        style={{ color: styles.text, background: styles.bg }}
      >
        <span className={`whitespace-nowrap ${fit}`}>{label}</span>
      </div>
    );
  }
  return (
    <div
      className={`inline-flex ${text} items-center ${fit} ${h} m-0 rounded-[10px] ${xs ? "px-[6px] pr-[8px]" : "px-[7px] pr-[9px]"} whitespace-nowrap`}
      style={{ color: styles.text, background: styles.bg }}
    >
      <div className={`inline-flex items-center ${fit} ${h} whitespace-nowrap`} style={{ lineHeight: xs ? "18px" : "20px" }}>
        <div className="flex items-center flex-shrink-0">
          <div className={`inline-flex shrink-0 rounded-full ${xs ? "h-[6px] w-[6px] mr-[4px]" : "h-[8px] w-[8px] mr-[5px]"}`} style={{ backgroundColor: styles.dot }} />
        </div>
        <span className={`whitespace-nowrap ${fit}`}>{label}</span>
      </div>
    </div>
  );
}

/** The pill variant a property type uses: status keeps a dot, others are chips. */
export function variantForType(type: string): PillVariant {
  return type === "status" ? "status" : "chip";
}

/** Status pill: rounded, dot + label. */
function StatusPill({ schema, value, size, noShrink }: { schema: OptionSchema; value: string; size?: PillSize; noShrink?: boolean }) {
  return <div className={`inline-flex ${noShrink ? "shrink-0" : "min-w-0 max-w-full"}`}><OptionPill variant="status" size={size} noShrink={noShrink} colorName={optionColorName(schema, value)} label={resolveOptionName(schema, value)} /></div>;
}

/** Select / priority chip: square-ish pill, no dot. */
function Chip({ schema, value, size, noShrink }: { schema: OptionSchema; value: string; size?: PillSize; noShrink?: boolean }) {
  return <div className={`inline-flex ${noShrink ? "shrink-0" : "min-w-0 max-w-full"}`}><OptionPill variant="chip" size={size} noShrink={noShrink} colorName={optionColorName(schema, value)} label={resolveOptionName(schema, value)} /></div>;
}

/** Multi-select chips; outside a view they stay capped with a "+N" overflow. */
function MultiSelectChips({ schema, values, max = 3, size, wrapMode }: { schema: OptionSchema; values: any[]; max?: number; size?: PillSize; wrapMode?: ContentWrapMode }) {
  const nowrap = wrapMode === "nowrap";
  const shown = wrapMode ? values : values.slice(0, max);
  return (
    <div className={`flex gap-1 min-w-0 max-w-full ${nowrap ? "flex-nowrap overflow-hidden" : "flex-wrap"}`}>
      {shown.map((item, idx) => (
        <OptionPill key={idx} variant="chip" size={size} noShrink={nowrap} colorName={optionColorName(schema, String(item))} label={resolveOptionName(schema, String(item))} />
      ))}
      {!wrapMode && values.length > max && (
        <span className={`shrink-0 ${size === "xs" ? "text-[11px]" : "text-[12px]"} text-[color:var(--c-texTer,#9b9a97)]`}>+{values.length - max}</span>
      )}
    </div>
  );
}

interface PropertyOptionDisplayProps {
  type: OptionType;
  propSchema?: OptionSchema | null;
  value: any;
  /** Max chips before "+N" (multi_select only). */
  maxItems?: number;
  /** Pill size — xs in view cards to match the other card properties. */
  size?: PillSize;
  wrapMode?: ContentWrapMode;
}

/**
 * Shared display for option-based property values (status, select, priority,
 * multi_select). Renders identically across every view/modal.
 */
export function PropertyOptionDisplay({ type, propSchema, value, maxItems, size, wrapMode }: PropertyOptionDisplayProps) {
  const schema = propSchema || {};
  const noShrink = wrapMode === "nowrap";

  if (type === "multi_select") {
    // Deleted options are dropped, not shown as raw ids
    const values = toItemList(value).filter((v) => !isDeletedOption(schema, v));
    if (values.length === 0) return null;
    return <MultiSelectChips schema={schema} values={values} max={maxItems ?? 3} size={size} wrapMode={wrapMode} />;
  }

  const single = String(value ?? "");
  if (!single) return null;

  if (type === "status") {
    if (isDeletedOption(schema, single)) return null;
    return <StatusPill schema={schema} value={single} size={size} noShrink={noShrink} />;
  }

  if (type === "priority") {
    const effective: OptionSchema = schema.options?.length ? schema : { options: DEFAULT_PRIORITY_OPTIONS };
    if (isDeletedOption(effective, single)) return null;
    return <Chip schema={effective} value={single} size={size} noShrink={noShrink} />;
  }

  if (isDeletedOption(schema, single)) return null;
  return <Chip schema={schema} value={single} size={size} noShrink={noShrink} />;
}
